<template>
	<ui-dropdown
		:button-class="buttonClass"
		:dropdown-class="dropdownClass"
		@opened="$emit('opened')"
		@close="$emit('close')"
	>
		<template v-slot:button>
			{{ buttonLabel }}
			<font-awesome-icon
				:icon="['fas', 'chevron-down']"
				class="ml-2 flex-shrink-0 h-3 w-3 -mr-1"
			/>
		</template>

		<template v-slot:items>
			<span class="block max-h-[60rem] overflow-y-auto overflow-x-hidden">
				<!-- Multi-Select: Checkboxen; @click.stop verhindert, dass ui-dropdown
				     beim Haken via closeDelayed() zuklappt. Leere Auswahl = kein Filter. -->
				<template v-if="multiple">
					<label
						class="flex items-center whitespace-nowrap px-4 py-2 text-gray-700 hover:bg-gray-100 cursor-pointer"
						v-for="option in options"
						:key="option.id"
						@click.stop
					>
						<input
							type="checkbox"
							class="h-4 w-4 rounded text-orange-500 border-gray-300 focus:ring-orange-400"
							:checked="isChecked(option.id)"
							@change="toggle(option.id)"
						/>
						<span class="ml-3 text-sm font-medium">{{ option.name }}</span>
					</label>
				</template>

				<!-- Single-Select: Radios + "kein Filter"; Klick darf (wie bisher) zuklappen. -->
				<template v-else>
					<div
						class="block px-4 py-2 text-gray-700"
						v-for="option in options"
						:key="option.id"
					>
						<div class="flex items-center">
							<input
								type="radio"
								class="focus:ring-orange-400 h-4 w-4 text-orange-500 border-gray-300"
								:id="radioId(option.id)"
								:checked="value === option.id"
								:name="radioName"
								@change="$emit('input', option.id)"
							/>
							<label
								class="ml-3 block text-sm font-medium text-gray-700 whitespace-nowrap"
								:for="radioId(option.id)"
							>{{ option.name }}</label>
						</div>
					</div>
					<div class="block px-4 py-2 text-gray-700">
						<div class="flex items-center">
							<input
								type="radio"
								class="focus:ring-orange-400 h-4 w-4 text-orange-500 border-gray-300"
								:id="radioId('none')"
								:checked="!value"
								:name="radioName"
								@change="$emit('input', null)"
							/>
							<label
								class="ml-3 block text-sm font-medium text-gray-700 whitespace-nowrap"
								:for="radioId('none')"
							>{{ __("No filter") }}</label>
						</div>
					</div>
				</template>
			</span>
		</template>
	</ui-dropdown>
</template>

<script>
/**
 * Wiederverwendbarer Filter-Dropdown für Tabellen-/Listen-Header.
 *
 * Zwei Modi über `multiple`:
 *   - false (Default): Radios + "kein Filter", emittiert die gewählte id bzw. null.
 *   - true: Checkboxen, emittiert ein Array der gewählten ids (leer = alle).
 *
 * Controlled: der aktuelle Wert kommt über `value` (v-model), der neue Wert geht
 * per `input`-Event zurück an den Parent (Single-Source-of-Truth bleibt dort).
 * Options erwartet bereits lokalisierte Namen ([{ id, name }]).
 */
export default {
	props: {
		// Basis-Label des Buttons (z.B. Spaltenname).
		label: {
			type: String,
			required: true,
		},
		// Auswahl-Optionen, Namen bereits lokalisiert.
		options: {
			type: Array,
			required: true,
		},
		// Aktueller Wert: id|null (single) bzw. Array von ids (multiple).
		value: {
			type: [String, Number, Array],
			default: null,
		},
		// true = Checkbox-Mehrfachauswahl, false = Radio-Einzelauswahl.
		multiple: {
			type: Boolean,
			default: false,
		},
		buttonClass: {
			type: String,
			default:
				"items-center inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-orange-500",
		},
		dropdownClass: {
			type: String,
			default:
				"origin-top-right absolute left-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-500",
		},
	},
	computed: {
		/**
		 * Normalisierte Mehrfachauswahl (immer ein Array).
		 *
		 * @returns {Array}
		 */
		selected() {
			return Array.isArray(this.value) ? this.value : [];
		},

		/**
		 * Eindeutiger Radio-Group-Name je Instanz (verhindert Kollision über mehrere Spalten).
		 *
		 * @returns {string}
		 */
		radioName() {
			return "filter-" + this._uid;
		},

		/**
		 * Button-Beschriftung: aktive Auswahl, sonst Basis-Label. Im Multi-Modus bei
		 * Teilauswahl mit Anzahl ("Status (2)"), bei leer/alle neutral das Basis-Label.
		 *
		 * @returns {string}
		 */
		buttonLabel() {
			if (this.multiple) {
				const count = this.selected.length;

				if (count === 0 || count === this.options.length) {
					return this.label;
				}

				return this.label + " (" + count + ")";
			}

			if (!this.value) {
				return this.label;
			}

			const option = this.options.find((o) => o.id === this.value);

			return option ? option.name : this.label;
		},
	},
	methods: {
		/**
		 * @param {string|number} id
		 * @returns {boolean}
		 */
		isChecked(id) {
			return this.selected.includes(id);
		},

		/**
		 * Schaltet eine Option in der Mehrfachauswahl um und emittiert das neue Array.
		 *
		 * @param {string|number} id
		 * @returns {void}
		 */
		toggle(id) {
			const next = this.isChecked(id)
				? this.selected.filter((v) => v !== id)
				: [...this.selected, id];

			this.$emit("input", next);
		},

		/**
		 * @param {string|number} id
		 * @returns {string}
		 */
		radioId(id) {
			return "radio-" + this._uid + "-" + id;
		},
	},
};
</script>
